(ns common.widgets.dropdown
  (:require ["@mui/material/Autocomplete$default" :as Autocomplete]
            ["@mui/material/Checkbox$default" :as Checkbox]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/InputAdornment$default" :as InputAdornment]
            ["@mui/material/ListSubheader$default" :as ListSubheader]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/TextField$default" :as TextField]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["react" :as react]
            ["react-use/lib/useMeasure$default" :as useMeasure]
            ["react-window" :as window]
            [cljs-bean.core :refer [->clj]]
            [clojure.spec.alpha :as spec]
            [common.util :refer [merge-refs spread-obj spread-props]]
            [common.widgets.interop :as interop]
            [common.widgets.truncated :as truncated]
            [goog.object :as gobject]
            [reagent.core :as r]
            [uix.core :as uix :refer [$ defui]]))

(spec/def :dropdown-option/value any?)
(spec/def :dropdown-option/label any?)
(spec/def :dropdown-option/on-click fn?)

(spec/def :dropdown/option (spec/or :value (spec/keys :req-un [:dropdown-option/value
                                                               :dropdown-option/label])
                                    :on-click (spec/keys :req-un [:dropdown-option/on-click
                                                                  :dropdown-option/label])))

(spec/def :dropdown/options (spec/nilable
                             (spec/and coll?
                                       (spec/coll-of :dropdown/option))))

(spec/def :dropdown/on-change fn?)

(spec/def :dropdown/props
  (spec/keys :req-un [:dropdown/options
                      :dropdown/on-change]))

(defui option-component [{:keys [render-checkbox? option state sx] :& props}]
  (let [[label-overflowing label-overflowing-ref] (truncated/use-overflowing)]
    ($ :div props
       ($ Stack
          {:direction "row"
           :alignItems "center"
           :gap 1
           :overflow "hidden"
           :sx sx}
          (when render-checkbox?
            ($ Checkbox {:checked (.-selected state)}))
          (when (.-icon option)
            ($ Icon {:fontSize "small"} (.-icon option)))
          ($ Tooltip
             {:disableHoverListener (not label-overflowing)
              :title (.-label option)}
             ($ Typography
                {:noWrap true
                 :ref label-overflowing-ref}
                (.-label option)))))))

;; Virtualization
;; From https://mui.com/material-ui/react-autocomplete/#virtualization
(defn create-list-component [render-checkboxes?]
  (uix/as-react
   (fn [{:keys [ref children] :as props}]
     (let [[measure-ref measure] (useMeasure)
           [height set-height] (uix/use-state nil)

           grouped? (.hasOwnProperty (first children) "group")
           children* (into []
                           (mapcat (fn list-component-mapcat [item]
                                     (cons item (.-children item))))
                           children)
           item-size 40
           item-count (count children*)]
       ;; Only use the first measured height.
       ;; The measured height can change when the options are filtered,
       ;; but we want to avoid using height of filtered content as then
       ;; the height doesn't go back up when the filter is cleared.
       (uix/use-effect (fn []
                         (when (and (nil? height)
                                    (> (.-height measure) 0))
                           (set-height (.-height measure))))
                       [height measure])
       ($ :div
          (spread-props props
                        {:ref (merge-refs ref measure-ref)
                         :style #js {:overflow "hidden"}})
          ($ window/FixedSizeList
             {:itemData children*
              ;; Max dropdown height seems to be ~850px on a 4k display.
              ;; Display initially with quite high content, so the PopperJs
              ;; can figure out the max height based on the content.
              :height 900
              ;; To avoid scrollbar, set the window height to the available
              ;; height. If we have less items than the measured available space,
              ;; just use the height for items we have.
              :style #js {:maxHeight (if (zero? height)
                                       "unset"
                                       (min (-> height
                                                ;; offset .MuiAutocomplete-listbox vertical padding
                                                (- (* 2 8)))
                                            (* item-size item-count)))}
              :width "100%"
              :itemSize item-size
              :itemCount item-count}
             (fn list-component-item [^js props]
               (let [style (.-style props)
                     data-set (nth (.-data props) (.-index props))]
                 (if (.hasOwnProperty data-set "group")
                   ($ ListSubheader
                      {:key (.-key data-set)
                       :component "div"
                       :style style
                       :sx #js {:backgroundColor "unset"}}
                      (.-group data-set))
                   (let [[props option state] data-set
                         props (->clj props)]
                     ($ option-component
                        {:style style
                         :option option
                         :state state
                         :render-checkbox? render-checkboxes?
                         :sx (when grouped?
                               #js {:pl 1})
                         :& props})))))))))))

(defn- render-option-int
  [^js props ^js option ^js state]
  (when-let [on-click (gobject/get option "on-click")]
    (set! (.-onClick props) on-click))
  [props option state])

(defn- chip-component
  [^js props ^js option]
  [:> Chip
   (cond-> (js->clj props)
     true (assoc :label (.-label option))
     (.-icon option) (assoc :icon (r/as-element [:> Icon (.-icon option)]))
     (.-locked option) (dissoc "onDelete"))])

(defn- fast-build-options
  "Optimized conversion to avoid slow clj->js call in Reagent.
  Use for dropdown where options list is thousands of items.

  Doesn't support extra properties.

  Enable this with fast-options-conversion flag."
  [options]
  (let [arr #js []]
    (doseq [item options]
      (.push arr #js {;; Value might be keyword
                      :value (clj->js (:value item))
                      :label (:label item)
                      :on-click (:on-click item)
                      :disabled? (:disabled? item)
                      :icon (:icon item)}))
    arr))

(defn dropdown
  [{:keys [options on-change value value-fn value-renderer filter-options size error data-testid class
           auto-focus? clearable? disabled? multi? free-solo? open-on-focus? placeholder limit-tags render-checkboxes?
           fast-options-conversion render-option text-field-props input-props disable-portal new-option-label] :as props
    :or {multi? false
         free-solo? false
         clearable? false
         auto-focus? false
         open-on-focus? false
         disable-portal true
         new-option-label "New label"
         filter-options (fn dropdown-filter-options [^js options ^js state]
                          (let [input (.toLowerCase (.-inputValue state))]
                            (if (seq input)
                              (.filter options (fn dropdown-filter-test [option]
                                                 (or (when (.-value option)
                                                       (or (= (.-value option) "nothing")
                                                           (.. option -value toString toLowerCase (includes input))))
                                                     (.. option -label toLowerCase (includes input)))))
                              options)))
         size "small"
         value-fn :value
         data-testid "dropdown"}}]
  ;; This takes 2.5 seconds on dev bulld e.g. location - City targetting trigger select.
  ;; Comment off when performance profiling for problems.
  {:pre [(spec/assert :dropdown/props props)]}
  (let [value->option (fn dropdown-value-option [value]
                        (or (some #(when (= (value-fn %) value) %) options)
                            (when free-solo?
                              {:value value :label value})))
        value-renderer (or value-renderer
                           (fn fast-value-renderer [^js item]
                             (if (string? item) item (.-label item))))
        value (-> (if multi?
                    (keep value->option value)
                    (value->option value))
                  clj->js)
        filter-options (if free-solo?
                         (let [base-filter filter-options]
                           (fn free-solo-filter [^js options ^js state]
                             (let [filtered (base-filter options state)
                                   input (.. state -inputValue trim)]
                               (if (and (seq input)
                                        (not (some #(= (.. % -label toLowerCase) (.toLowerCase input)) filtered)))
                                 (let [new-opt (clj->js {:value input :label (str input " (" new-option-label ")") :new? true})]
                                   (.concat filtered #js [new-opt]))
                                 filtered))))
                         filter-options)
        is-option-equal-to-value (if (and (keyword? value-fn)
                                          ;; This fast version only works if the value is basic JS type, like string or number,
                                          ;; the default transform converts Clj maps into JS objects, and equals check doens't
                                          ;; work for them unless doing full js->clj first.
                                          fast-options-conversion)
                                   (let [prop-name (name value-fn)
                                         getter (if (= :value value-fn)
                                                  #(.-value %)
                                                  #(gobject/get % prop-name))]
                                     (fn fast-dropdown-is-option-equal-to-value
                                       [^js option ^js value]
                                       (= (getter option) (getter value))))
                                   (fn slow-dropdown-is-option-equal-to-value
                                     [^js option ^js value]
                                     (= (-> option (js->clj :keywordize-keys true) value-fn)
                                        (-> value (js->clj :keywordize-keys true) value-fn))))]
    [:> Autocomplete
     (merge
      (dissoc props :value-fn :value-renderer :error :placeholder :disabled? :clearable? :multi? :free-solo? :new-option-label :open-on-focus? :auto-focus? :render-checkboxes? :text-field-props :input-props
              :fast-options-conversion)
      {;; Options value conversion must happen here because e.g. value->option wants to access the cljs collection
       :options (if fast-options-conversion
                  (fast-build-options options)
                  options)
       :value value
       :get-option-label value-renderer
       :is-option-equal-to-value is-option-equal-to-value
       :class (cond-> []
                class (conj class)
                multi? (conj "MuiAutocomplete-multi")
                true flatten)
       :data-testid data-testid
       :disabled disabled?
       :disable-clearable (not clearable?)
       :disable-portal disable-portal
       :filter-options filter-options
       :freeSolo free-solo?
       :autoSelect false
       :multiple multi?
       :open-on-focus open-on-focus?
       :select-on-focus false
       :size size
       :ListboxComponent (when (not render-option) (create-list-component render-checkboxes?))
       :get-option-disabled (fn get-option-disabled [^js option]
                              (gobject/get option "disabled?"))
       :render-input (fn dropdown-render-input [^js props]
                       (let [input-el-props
                             (spread-obj (.-inputProps props)
                                         #js {:data-lpignore true})

                             input-comp-props
                             (spread-obj (.-InputProps props)
                                         ;; Autocomplete -> TextField -> Input -> html input
                                         ;; This will tell the MUI Input component to use html
                                         ;; input element with Reagent workaround.
                                         #js {:inputComponent interop/input-component}
                                         (when (and value (.-icon value))
                                           #js {:startAdornment ($ InputAdornment
                                                                   {:position "start"}
                                                                   ($ Icon (.-icon value)))})
                                         (clj->js (if (fn? input-props)
                                                    (input-props props)
                                                    input-props)))

                             new-props
                             (spread-props props
                                           #js {:error error
                                                :placeholder placeholder
                                                :autoFocus auto-focus?
                                                :inputProps input-el-props
                                                :InputProps (spread-props input-comp-props
                                                                          (:InputProps text-field-props))}
                                           (dissoc text-field-props :InputProps))]
                         (react/createElement TextField new-props)))
       :render-option (or render-option render-option-int)
       :render-group (fn dropdown-render-group [params]
                       params)
       :render-tags (fn dropdown-render-tags [^js value ^js get-tag-props]
                      (r/as-element
                       [:<>
                        (cond->> value
                          (nat-int? limit-tags) (take limit-tags)
                          true (map-indexed (fn [index ^js option]
                                              ^{:key index} [chip-component (get-tag-props #js {:index index}) option])))
                        (when (and limit-tags (> (count value) limit-tags))
                          (str "+" (- (count value) limit-tags)))]))
       :on-change (fn dropdown-on-change [^js _event ^js js-value]
                    (let [value (js->clj js-value :keywordize-keys true)]
                      (on-change (if multi?
                                   (let [locked-options (keep (fn [{:keys [locked value]}] (when locked value)) options)
                                         extract-value (fn [v]
                                                         (if (string? v)
                                                           v
                                                           (or (value-fn v) v)))]
                                     (-> (mapv extract-value value)
                                         (concat locked-options)
                                         (distinct)
                                         (vec)))
                                   (if (string? value)
                                     value
                                     (value-fn value))))))
       :on-key-down (when-not multi?
                      (fn dropdown-on-key-down [^js event]
                        (when (= (.-key event) "Enter")
                          (when-let [v (some #(when (= (.. (value-fn %) toString toLowerCase)
                                                       (.. event -target -value toLowerCase)) %) options)]
                            (on-change (value-fn v))))))})]))

